import { useCallback } from 'react';
import { LocalAccount } from '../../../types/LocalAccount';
import { Icons, showAlert } from '@silverfort/sf-components';
import { useExcludeLocalAccounts, ExcludeLocalAccountsResponse } from '../../../api';
import useExcludePopup from '../../../hooks/useExcludePopup';
import { useSnackAlert } from '../../../context/SnackAlertProvider';

import { SnackAlertContextValue } from '../../../context/SnackAlertProvider';

interface UseExcludeLocalAccountPopupParams {
    account: LocalAccount;
    onAccountExcluded?: (excludeTill: LocalAccount['excludeTill']) => void;
    snackAlertContext: SnackAlertContextValue;
}

interface UseExcludeLocalAccountsPopupParams {
    accounts: LocalAccount[];
    onAccountsExcluded?: () => void;
}

interface ReturnType {
    openExcludeLocalAccountsPopup: () => void;
}

const getExcludeLocalAccountsDescription = (accounts: LocalAccount[]): string => {
    if (!accounts.length) {
        return '';
    }

    if (accounts.length > 1) {
        return 'How long do you want to exclude the selected local accounts?';
    }

    return `For how long do you want to exclude ${accounts[0].userName} local account?`;
};

export const useExcludeLocalAccountPopup = ({
    account,
    onAccountExcluded,
    snackAlertContext
}: UseExcludeLocalAccountPopupParams): ReturnType => {
    const { openSnackAlert, closeSnackAlert } = snackAlertContext;

    const handleAccountsExcluded = useCallback(
        (response: ExcludeLocalAccountsResponse) => {
            openSnackAlert({
                message: `${account.userName} was temporarily excluded`,
                iconLeft: Icons.Block,
                onClose: closeSnackAlert
            });
            onAccountExcluded?.(response.excludeTill);
        },
        [account, onAccountExcluded, openSnackAlert, closeSnackAlert]
    );

    const handleError = useCallback(() => {
        showAlert('Failed to exclude local account, please try again', 'error');
    }, []);

    const { excludeLocalAccounts } = useExcludeLocalAccounts({
        onSuccess: handleAccountsExcluded,
        onError: handleError
    });

    const handleExclude = useCallback(
        (excludeTill: Date) => {
            excludeLocalAccounts([account.guid], excludeTill);
        },
        [account, excludeLocalAccounts]
    );

    const { openExcludePopup } = useExcludePopup({
        title: 'Exclude local account',
        description: `How long do you want to exclude ${account.userName}?`,
        customExcludeDescription: `How long do you want to exclude ${account.userName}?`,
        onExclude: handleExclude
    });

    return { openExcludeLocalAccountsPopup: openExcludePopup };
};

export const useExcludeLocalAccountsPopup = ({
    accounts,
    onAccountsExcluded
}: UseExcludeLocalAccountsPopupParams): ReturnType => {
    const { openSnackAlert, closeSnackAlert } = useSnackAlert();

    const handleAccountsExcluded = useCallback(() => {
        openSnackAlert({
            message: 'Local accounts were temporarily excluded',
            iconLeft: Icons.Block,
            onClose: closeSnackAlert
        });
        onAccountsExcluded?.();
    }, [onAccountsExcluded, openSnackAlert, closeSnackAlert]);

    const handleError = useCallback(() => {
        showAlert('Failed to exclude local accounts, please try again', 'error');
    }, []);

    const { excludeLocalAccounts } = useExcludeLocalAccounts({
        onSuccess: handleAccountsExcluded,
        onError: handleError
    });

    const handleExclude = useCallback(
        (excludeTill: Date) => {
            excludeLocalAccounts(
                accounts.map(({ guid }) => guid),
                excludeTill
            );
        },
        [accounts, excludeLocalAccounts]
    );

    const { openExcludePopup } = useExcludePopup({
        title: 'Exclude local accounts',
        description: getExcludeLocalAccountsDescription(accounts),
        customExcludeDescription: 'How long do you want to exclude the selected local accounts?',
        onExclude: handleExclude
    });

    return { openExcludeLocalAccountsPopup: openExcludePopup };
};
